import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAnalyticsFiltersStore } from '../../../features/Analytics/stores/useAnalyticsFiltersStore/useAnalyticsFiltersStore'
import { LinkButton } from '../../common'
import { useFeatureFlag } from '@/components/providers/FeatureFlagsProvider/useFeatureFlag'
import { FilterToggleButton } from '@/components/common/FilterToggleButton/FilterToggleButton'
import { useActiveFiltersCount } from '@/hooks/useActiveFilterCount/useActiveFilterCount'
import { DEFAULT_ANALYTICS_FILTERING } from '@/utils/constants'
import { ActiveFiltersCounter } from '@/components/common/ActiveFiltersCounter/ActiveFiltersCounter'
import { BenchmarkingDisplay, Calculation, useFiltersApi } from '@/hooks'
import {
    FiltersStoreState,
    createSelectHasDefaultFilters,
} from '@/stores/createFiltersStore'
import { TimelineFilterValue } from '@/components/common/Filter/types'
import { ReportFiltersValues } from '@/features/Analytics/stores/useAnalyticsFiltersStore/types'
import { CommodityFilter } from '@/components/core/filters/CommodityFilter'
import { ProviderFilter } from '@/components/core/filters/ProviderFilter'
import { NodeFilter } from '@/components/core/filters/NodeFilter'
import { ContractFilter } from '@/components/core/filters/ContractFilter'
import { AccountFilter } from '@/components/core/filters/AccountFilter'
import { TagFilter } from '@/components/core/filters/TagFilter'
import { FacilityFilter } from '@/components/core/filters/FacilityFilter'
import { TimelineFilter } from '@/components/core/filters/TimelineFilter'
import { USNFilter } from '@/components/core/filters/USNFilter'
import { MeterFilter } from '@/components/core/filters/MeterFilter'
import { useLocation } from '@tanstack/react-router'
import { useAnalyticsFiltersGetParams } from '@/features/Analytics/hooks/useAnalyticsFiltersGetParams'
import { CalculationFilter } from '@/components/core/filters/CalculationFilter'
import useAnalyticsStore from '@/features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import classnames from 'classnames'
import { UnitOfMeasureFilter } from '@/components/core/filters/UnitOfMeasureFilter'
import { applyStandardUOM } from './applyStandardUOM'

export type AnalyticsFiltersContainerProps = {
    activeFilters: ReportFiltersValues
    onChange: FiltersStoreState<ReportFiltersValues>['updateActiveFilter']
    isBenchmarking?: boolean
    selectedFilter?: keyof ReportFiltersValues
    showResetFiltersButton?: boolean
    handleReset?: () => void
}

export const AnalyticsFiltersContainer: React.FC<
    AnalyticsFiltersContainerProps
> = (props) => {
    const {
        activeFilters,
        onChange,
        isBenchmarking,
        selectedFilter,
        showResetFiltersButton,
        handleReset,
    } = props
    const { t } = useTranslation('translations')
    const containerRef = useRef<HTMLDivElement>(null)
    const [isVisible, setIsVisible] = useState(true)

    const isCalcultionFilterEnabled = useFeatureFlag(
        'analytics.usageAndCost.calculationFilter'
    )

    const activeFiltersCount = useActiveFiltersCount(
        activeFilters,
        DEFAULT_ANALYTICS_FILTERING
    )

    const handleToggleVisibility = () => {
        setIsVisible((prevVisible) => !prevVisible)
    }

    const { pathname } = useLocation()

    const isUSNNotAvailable = useMemo(() => {
        return pathname.includes('/facility')
    }, [pathname])

    const isMeterNotAvailable = useMemo(() => {
        return pathname.includes('/gap') || pathname.includes('/facility')
    }, [pathname])

    const params = useAnalyticsFiltersGetParams(activeFilters)

    const hoveredDisplaySetting = useAnalyticsStore(
        (state) => state.benchmarking.hoveredDisplaySetting
    )

    const getBenchmarkingFilterHighlightClass = useCallback(
        (displaySetting: BenchmarkingDisplay) => {
            if (!isBenchmarking) return undefined

            const filterType =
                displaySetting === 'group' ? 'tag' : displaySetting
            const isSelected = selectedFilter === filterType
            const isHovered = hoveredDisplaySetting === displaySetting

            return classnames({
                'outline outline-4 outline-blue-200': isHovered,
                '!shadow-inset-azure !border-azure': isSelected,
            })
        },
        [isBenchmarking, selectedFilter, hoveredDisplaySetting]
    )

    const { useCommoditiesQuery } = useFiltersApi()
    const commoditiesQuery = useCommoditiesQuery({
        'nodeIds[]': params['nodeIds[]'],
        ...(isBenchmarking && { reportType: 'benchmarking' }),
    })
    const { data, isLoading, isFetched, isFetching, isError } = commoditiesQuery

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-4" ref={containerRef}>
                <div
                    className="flex items-center justify-between gap-2"
                    data-testid="analytics-filters-title-row"
                >
                    <div className="text-heading-6 py-2.5">
                        {t('filters.title')}
                    </div>
                    {!isVisible && (
                        <ActiveFiltersCounter count={activeFiltersCount} />
                    )}
                </div>
                {isVisible && (
                    <>
                        <NodeFilter
                            name="analytics-node-filter"
                            value={activeFilters.node.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_ANALYTICS_FILTERING.node.map(
                                (id) => id.toString()
                            )}
                            containerRef={containerRef}
                            onChange={(value: string[]) => {
                                onChange(
                                    'node',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            className={getBenchmarkingFilterHighlightClass(
                                'node'
                            )}
                        />
                        <CommodityFilter
                            name="analytics-commodity-filter"
                            containerRef={containerRef}
                            multiple={false}
                            value={activeFilters.commodity.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_ANALYTICS_FILTERING.commodity.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                const commodityId = parseInt(value[0])
                                onChange('commodity', [commodityId])

                                applyStandardUOM(
                                    commodityId,
                                    data,
                                    activeFilters,
                                    onChange
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                ...(isBenchmarking && {
                                    reportType: 'benchmarking',
                                }),
                            }}
                        />

                        <UnitOfMeasureFilter
                            name="analytics-uom-filter"
                            containerRef={containerRef}
                            value={(activeFilters.unitOfMeasureId ?? []).map(
                                (id) => id.toString()
                            )}
                            defaultValue={(
                                DEFAULT_ANALYTICS_FILTERING.unitOfMeasureId ??
                                []
                            ).map((id) => id.toString())}
                            onChange={(value: string[]) => {
                                onChange(
                                    'unitOfMeasureId',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            selectedCommodityTypeId={activeFilters.commodity[0]}
                            multiple={false}
                            isLoading={isLoading || isFetching || !isFetched}
                            isError={isError}
                            commodities={data}
                        />

                        <ProviderFilter
                            name="analytics-provider-filter"
                            containerRef={containerRef}
                            value={(activeFilters.provider ?? []).map((id) =>
                                id.toString()
                            )}
                            defaultValue={(
                                DEFAULT_ANALYTICS_FILTERING.provider ?? []
                            ).map((id) => id.toString())}
                            onChange={(value: string[]) => {
                                onChange(
                                    'provider',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]': params.commodityTypeId,
                            }}
                        />
                        <ContractFilter
                            name="analytics-contract-filter"
                            containerRef={containerRef}
                            value={activeFilters.contract.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_ANALYTICS_FILTERING.contract.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'contract',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]': params.commodityTypeId,
                                'providerIds[]': params['providerIds[]'],
                            }}
                        />
                        <AccountFilter
                            name="analytics-account-filter"
                            containerRef={containerRef}
                            value={(activeFilters.account ?? []).map((id) =>
                                id.toString()
                            )}
                            defaultValue={(
                                DEFAULT_ANALYTICS_FILTERING.account ?? []
                            ).map((id) => id.toString())}
                            onChange={(value: string[]) => {
                                onChange(
                                    'account',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]': params.commodityTypeId,
                                'providerIds[]': params['providerIds[]'],
                                'contractIds[]': params['contractIds[]'],
                            }}
                        />
                        <TagFilter
                            name="analytics-tag-filter"
                            containerRef={containerRef}
                            value={activeFilters.tag.map((id) => id.toString())}
                            defaultValue={DEFAULT_ANALYTICS_FILTERING.tag.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'tag',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            className={getBenchmarkingFilterHighlightClass(
                                'group'
                            )}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]': params.commodityTypeId,
                                'providerIds[]': params['providerIds[]'],
                                'contractIds[]': params['contractIds[]'],
                                'panIds[]': params['panIds[]'],
                            }}
                        />
                        <FacilityFilter
                            name="analytics-facility-filter"
                            containerRef={containerRef}
                            value={activeFilters.facility.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_ANALYTICS_FILTERING.facility.map(
                                (id) => id.toString()
                            )}
                            onChange={(value: string[]) => {
                                onChange(
                                    'facility',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            className={getBenchmarkingFilterHighlightClass(
                                'facility'
                            )}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]': params.commodityTypeId,
                                'providerIds[]': params['providerIds[]'],
                                'contractIds[]': params['contractIds[]'],
                                'panIds[]': params['panIds[]'],
                                'tagIds[]': params['tagIds[]'],
                            }}
                        />
                        <USNFilter
                            name="analytics-usn-filter"
                            containerRef={containerRef}
                            value={(activeFilters.usn ?? []).map((id) =>
                                id.toString()
                            )}
                            defaultValue={(
                                DEFAULT_ANALYTICS_FILTERING.usn ?? []
                            ).map((id) => id.toString())}
                            notAvailable={isUSNNotAvailable}
                            onChange={(value: string[]) => {
                                onChange(
                                    'usn',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            className={getBenchmarkingFilterHighlightClass(
                                'usn'
                            )}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]': params.commodityTypeId,
                                'providerIds[]': params['providerIds[]'],
                                'contractIds[]': params['contractIds[]'],
                                'panIds[]': params['panIds[]'],
                                'tagIds[]': params['tagIds[]'],
                                'facilityIds[]': params['facilityIds[]'],
                            }}
                        />
                        <MeterFilter
                            name="analytics-meter-filter"
                            containerRef={containerRef}
                            value={activeFilters.meter.map((id) =>
                                id.toString()
                            )}
                            defaultValue={DEFAULT_ANALYTICS_FILTERING.meter.map(
                                (id) => id.toString()
                            )}
                            notAvailable={isMeterNotAvailable}
                            onChange={(value: string[]) => {
                                onChange(
                                    'meter',
                                    value.map((id) => parseInt(id))
                                )
                            }}
                            className={getBenchmarkingFilterHighlightClass(
                                'meter'
                            )}
                            params={{
                                'nodeIds[]': params['nodeIds[]'],
                                'commodityTypeIds[]': params.commodityTypeId,
                                'providerIds[]': params['providerIds[]'],
                                'contractIds[]': params['contractIds[]'],
                                'panIds[]': params['panIds[]'],
                                'tagIds[]': params['tagIds[]'],
                                'facilityIds[]': params['facilityIds[]'],
                                'usnAccountIds[]': params['usnAccountIds[]'],
                            }}
                        />
                        <TimelineFilter
                            name="analytics-timeline-filter"
                            value={activeFilters.timeline}
                            defaultValue={DEFAULT_ANALYTICS_FILTERING.timeline}
                            onChange={(value: TimelineFilterValue) => {
                                onChange('timeline', value)
                            }}
                            containerRef={containerRef}
                        />
                        {isCalcultionFilterEnabled && (
                            <CalculationFilter
                                name="analytics-calculation-filter"
                                containerRef={containerRef}
                                value={'productionMonth'}
                                defaultValue={'productionMonth'}
                                onChange={(value: Calculation) => {
                                    onChange('calculation', value)
                                }}
                            />
                        )}
                    </>
                )}
                {showResetFiltersButton && (
                    <LinkButton
                        name="reset-filters"
                        iconBefore="sync"
                        label={t('filters.reset')}
                        onClick={handleReset}
                    />
                )}
                <FilterToggleButton
                    isVisible={isVisible}
                    onToggle={handleToggleVisibility}
                />
            </div>
        </div>
    )
}

type AnalyticsFiltersProps = {
    selectedFilter?: keyof ReportFiltersValues
}

export const AnalyticsFilters: React.FC<AnalyticsFiltersProps> = (props) => {
    const { selectedFilter } = props
    const { pathname } = useLocation()

    const updateActiveFilter = useAnalyticsFiltersStore(
        (state) => state.updateActiveFilter
    )

    const activeFilters = useAnalyticsFiltersStore(
        (state) => state.activeFilters
    )

    const handleResetActiveFilters = useAnalyticsFiltersStore(
        (state) => state.resetActiveFilters
    )
    const selectHasDefaultFilters = createSelectHasDefaultFilters(
        DEFAULT_ANALYTICS_FILTERING
    )
    const showResetFiltersButton = !useAnalyticsFiltersStore(
        selectHasDefaultFilters
    )

    const isBenchmarking = pathname.includes('/benchmarking')

    return (
        <AnalyticsFiltersContainer
            onChange={updateActiveFilter}
            activeFilters={activeFilters}
            isBenchmarking={isBenchmarking}
            selectedFilter={selectedFilter}
            handleReset={handleResetActiveFilters}
            showResetFiltersButton={showResetFiltersButton}
        />
    )
}
